import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Download,
  Network,
  ArrowUpRight,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";
const organogramImage = "/assets/images/oganogram.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";

const OGFZAOrganogram = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.from(".organogram-card", {
        opacity: 0,
        y: 55,
        scale: 0.98,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".organogram-card",
          start: "top 85%",
        },
      });

      gsap.to(".floating-orb-1", {
        y: 45,
        x: -25,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -45,
        x: 25,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA Organogram"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[520px] h-[520px] rounded-full bg-[#082567]/45 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Organizational Structure
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            OGFZA Organogram
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            View the official organizational structure of the Oil and Gas Free
            Zones Authority, showing the Authority’s leadership and functional
            reporting framework.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">OGFZA Organogram</span>
          </div>

          <div className="hero-text flex flex-wrap items-center gap-4 mt-10">
            <a
              href={organogramImage}
              download="OGFZA-Organogram.jpg"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#F2AF11] hover:bg-white hover:text-[#082567] text-white font-black transition-all duration-500 shadow-xl"
            >
              <Download size={20} />
              Download Organogram
            </a>

            <a
              href="#organogram"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/10 hover:bg-white hover:text-[#082567] text-white border border-white/15 backdrop-blur-md font-black transition-all duration-500"
            >
              View Image
              <ArrowUpRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* ORGANOGRAM IMAGE */}
      <section id="organogram" className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Organogram Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.07]"
        />
        <div className="absolute inset-0 bg-white/92" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="organogram-card bg-white border border-slate-200 rounded-[36px] p-5 md:p-8 shadow-[0_26px_90px_-60px_rgba(8,37,103,0.65)]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-7">
              <div>
                <div className="inline-flex items-center gap-3 text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-3">
                  <Network size={18} className="text-[#F2AF11]" />
                  Official Structure
                </div>

                <h2
                  className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Organizational Chart
                </h2>
              </div>

              <a
                href={organogramImage}
                download="OGFZA-Organogram.jpg"
                className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500"
              >
                <Download size={20} />
                Download
              </a>
            </div>

            <div className="rounded-[28px] overflow-hidden bg-[#FAFBFC] p-3 md:p-5 border border-slate-100">
              <img
                src={organogramImage}
                alt="OGFZA Organogram"
                className="w-full h-auto object-contain rounded-[20px]"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OGFZAOrganogram;